
import { useState, useEffect } from 'react';
import { useSupabaseSEO } from '../../../hooks/useSupabaseSEO';

const SEOManager: React.FC = () => {
  const [selectedPage, setSelectedPage] = useState('home');
  const { seoData, loading, updateSEO } = useSupabaseSEO(selectedPage);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    keywords: '',
    og_image: '',
    canonical_url: ''
  });

  const pages = [
    { key: 'home', name: 'Home' },
    { key: 'brand', name: 'The Golden Tower' },
    { key: 'new-releases', name: 'Residences' },
    { key: 'amenities', name: 'Amenities' },
    { key: 'contact', name: 'Contact' }
  ];

  // Update form when SEO data changes
  useEffect(() => {
    if (seoData) {
      setFormData({
        title: seoData.title || '',
        description: seoData.description || '',
        keywords: seoData.keywords || '',
        og_image: seoData.og_image || '',
        canonical_url: seoData.canonical_url || ''
      });
    }
  }, [seoData]);

  const handleSave = async () => {
    try {
      await updateSEO({
        page_key: selectedPage,
        ...formData
      });
      alert('SEO settings saved successfully!');
    } catch (err) {
      alert('Failed to save SEO settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">SEO Settings</h2>
      </div>

      {/* Page Selector */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {pages.map((page) => (
            <button
              key={page.key}
              onClick={() => setSelectedPage(page.key)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                selectedPage === page.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {page.name}
            </button>
          ))}
        </nav>
      </div>

      {/* SEO Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Page Title
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Should be less than 60 characters"
            maxLength={60}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white"
          />
          <p className="text-xs text-gray-500 mt-1">{formData.title.length}/60 characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Meta Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Recommended between 120-160 characters"
            maxLength={160}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white"
          />
          <p className="text-xs text-gray-500 mt-1">{formData.description.length}/160 characters</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Keywords
          </label>
          <input
            type="text"
            value={formData.keywords}
            onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
            placeholder="keyword1, keyword2, keyword3"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white"
          />
          <p className="text-xs text-gray-500 mt-1">Enter 3-5 keywords separated by commas</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Open Graph Image URL (OG Image)
          </label>
          <input
            type="text"
            value={formData.og_image}
            onChange={(e) => setFormData({ ...formData, og_image: e.target.value })}
            placeholder="https://example.com/image.jpg"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Canonical URL
          </label>
          <input
            type="text"
            value={formData.canonical_url}
            onChange={(e) => setFormData({ ...formData, canonical_url: e.target.value })}
            placeholder="https://example.com/page"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white"
          />
        </div>

        <div className="flex justify-end pt-4">
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap cursor-pointer"
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* SEO Preview */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Google Search Preview</h3>
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-blue-600 text-xl mb-1">{formData.title || 'Page Title'}</div>
          <div className="text-green-700 text-sm mb-2">
            {formData.canonical_url || 'https://example.com/page'}
          </div>
          <div className="text-gray-600 text-sm">
            {formData.description || 'Page description will appear here...'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SEOManager;
